//by passing context/state as part of the message to Soundslice, we will get that state back when they send a message back.  This allows us to resolve promises
import {SoundSliceVideoPositionEnum} from "../../soundSlice/soundSliceVideoPositionEnum";

export interface ISoundSliceRequestContext {
  requestId: string; //generateUUID.
}

export interface ISoundSliceRequestWithState {
  state?: ISoundSliceRequestContext;
}

export interface IGetNotes extends ISoundSliceRequestWithState{
  type: "getNotes";
  recordingIndex: number;
}

export interface IPlay extends ISoundSliceRequestWithState{
  type: "play";
}

export interface IPause extends ISoundSliceRequestWithState {
  type: "pause";
}

export interface ISeek extends ISoundSliceRequestWithState {
  type: "seek";
  offset: number; //typically a second
}

export interface ISetSpeed extends ISoundSliceRequestWithState {
  type: "setSpeed";
  speed: number; //speed = 1; //incremented/decremented by .1
}

export interface ISetVolume extends ISoundSliceRequestWithState {
  type: "setVolume";
  volume: number; // 0.5 for 50%, 1 for 100%, etc.
}

export interface IGetDuration extends ISoundSliceRequestWithState {
  type: "getDuration";
}

export interface IGetBarCount extends ISoundSliceRequestWithState{
  type: "getBarCount";
}

export interface IChangeAudioByIndex extends ISoundSliceRequestWithState{
  type: "changeAudioByIndex";
  recordingIndex: number;
}

export interface ISetTrackVisibility extends ISoundSliceRequestWithState{
  type: "setTrackVisibility";
  visibilityType: SoundsliceTrackVisibilityTypes;
  isVisible: boolean;
}

export enum SoundsliceTrackVisibilityTypes {
  StaffNotation = 1,
  Tablature = 2,
  TablatureStems = 3,
  ChordNames = 4,
  ChordDiagrams = 5,
  Lyrics = 6,
  Fingering = 7,
  PitchNames = 9,
  RhythmCounts = 18,
}

export interface ISetZoom extends ISoundSliceRequestWithState{
  type: "setZoom";
  zoom: number; // -25 to 50
}

export interface IGetCurrentTime extends ISoundSliceRequestWithState{
  type: "getCurrentTime";
}

export interface ISetLoop extends ISoundSliceRequestWithState{
  type: "setLoop";
  startTimeSeconds: number;
  endTimeSeconds: number;
}

export interface IClearLoop extends ISoundSliceRequestWithState{
  type: "clearLoop";
}

export interface IEditNote extends ISoundSliceRequestWithState{
  type: "editNote";
  barIndex: number;
  trackIndex: number;
  voiceIndex: number;
  beatIndex: number;
  noteIndex: number;
  color: string;
}

export interface ISetVideoPosition extends ISoundSliceRequestWithState{
  type: "setVideoPosition";
  position: SoundSliceVideoPositionEnum;
}

export interface IDrawTempNote extends ISoundSliceRequestWithState{
  type: "drawTempNote";
  barIndex: number;
  trackIndex: number;
  midi: number;
  barOffsetPercent: number;
  color: string;
}

export interface IClearTempNotes extends ISoundSliceRequestWithState{
  type: "clearTempNotes";
}

export interface ILoadAudioByIndex extends ISoundSliceRequestWithState{
  type: "loadAudioByIndex";
  recordingIndex: number;
}

export interface ISetPlayheadType extends ISoundSliceRequestWithState {
  type: "setPlayheadType";
  playheadType: number;
}

export interface ISetHighlightCurrentBar extends ISoundSliceRequestWithState {
  type: "setHighlightCurrentBar";
  highlightCurrentBar: boolean;
}

export interface ISetProportionalNotation extends ISoundSliceRequestWithState {
  type: "setProportionalNotation";
  isNotationProportional: boolean;
}

export interface ISetProportionalSize extends ISoundSliceRequestWithState {
  type: "setProportionalSize";
  proportionalSize: number;
}

export interface ISetNotationLayout extends ISoundSliceRequestWithState {
  type: "setLayout";
  layout: number;
}

export interface ISetPageCount extends ISoundSliceRequestWithState {
  type: "setPagesPerScreen";
  pageCount: number;
}

export type SoundsliceRequestEventTypes =
  IChangeAudioByIndex
  | IClearLoop
  | IClearTempNotes
  | IDrawTempNote
  | IEditNote
  | IGetBarCount
  | IGetCurrentTime
  | IGetDuration
  | IGetNotes
  | ILoadAudioByIndex
  | IPause
  | IPlay
  | ISeek
  | ISetHighlightCurrentBar
  | ISetLoop
  | ISetNotationLayout
  | ISetPageCount
  | ISetPlayheadType
  | ISetProportionalNotation
  | ISetProportionalSize
  | ISetSpeed
  | ISetTrackVisibility
  | ISetVideoPosition
  | ISetVolume
  | ISetZoom;